/**
 * LifecycleStepInfo Component
 *
 * Read-only panel of a lifecycle step that has no settings of its own.
 */
import { Button, Icon } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { stepLabelKey, type LifecycleDocument, type LifecycleStepKey } from './types';

export interface LifecycleStepInfoProps {
  /** Document the step belongs to. */
  document: LifecycleDocument;
  /** Step whose title, description and note are shown. */
  stepKey: LifecycleStepKey;
  /** Renders a shortcut to the integrations tab. */
  onOpenIntegrations?: () => void;
}

export function LifecycleStepInfo({
  document,
  stepKey,
  onOpenIntegrations
}: LifecycleStepInfoProps) {
  const t = useFeatureTranslation('systemSettings.automation.lifecycle');
  const labelKey = stepLabelKey(document, stepKey);

  return (
    <div className='space-y-lg'>
      <div>
        <h2 className='text-xl font-bold text-foreground-base'>{t(`steps.${labelKey}.title`)}</h2>
        <p className='text-sm text-foreground-muted mt-xs'>{t(`steps.${labelKey}.description`)}</p>
      </div>

      <div
        data-testid={`lifecycle-info-${document}-${stepKey}`}
        className='flex items-start gap-sm rounded-xl border border-border-base bg-background-base p-md'
      >
        <Icon name='lock' className='w-4 h-4 flex-none mt-2xs text-foreground-disabled' />
        <div className='flex flex-col items-start gap-md'>
          <p className='text-sm text-foreground-muted'>{t(`steps.${labelKey}.note`)}</p>
          {onOpenIntegrations && (
            <Button
              variant='primary'
              size='lg'
              icon='arrows-rotate'
              onClick={onOpenIntegrations}
              testId='lifecycle-open-integrations'
            >
              {t('integrationSettings')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
